<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { AppButton } from '@/design-system/components'
import type { Note, CreateNoteRequest, UpdateNoteRequest } from '@/api'
import { useAnalytics } from '@/composables/useAnalytics'

const props = withDefaults(
  defineProps<{
    fetchNotes: () => Promise<Note[]>
    createNote: (data: CreateNoteRequest) => Promise<Note>
    updateNote: (noteId: string, data: UpdateNoteRequest) => Promise<Note>
    deleteNote: (noteId: string) => Promise<void>
    accentColor?: string
    placeholder?: string
    emptyMessage?: string
    /** Analytics event fired on note creation, e.g. 'class_note_added' | 'student_note_created'. */
    analyticsEvent?: string
  }>(),
  {
    accentColor: '#6366f1',
    placeholder: 'Add a quick note...',
    emptyMessage: 'No notes yet',
    analyticsEvent: undefined,
  }
)

const { capture } = useAnalytics()

const notes = ref<Note[]>([])
const loading = ref(true)
const error = ref<string | null>(null)
const sortNewest = ref(true)

// Quick input
const quickText = ref('')
const isSending = ref(false)

// Edit state
const editingId = ref<string | null>(null)
const editTitle = ref('')
const editText = ref('')
const isSavingEdit = ref(false)

// Delete state
const deletingId = ref<string | null>(null)
const isDeleting = ref(false)

// Menu state
const openMenuId = ref<string | null>(null)

function nowLocalDateTime(): string {
  // Backend expects LocalDateTime (no timezone), e.g. "2026-02-17T11:42:00"
  const now = new Date()
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
}

const sortedNotes = computed(() => {
  const sorted = [...notes.value].sort((a, b) => {
    const da = a.date ? new Date(a.date).getTime() : 0
    const db = b.date ? new Date(b.date).getTime() : 0
    return sortNewest.value ? db - da : da - db
  })
  return sorted
})

async function loadNotes() {
  loading.value = true
  error.value = null
  try {
    notes.value = await props.fetchNotes()
  } catch (err) {
    console.error('Failed to load notes:', err)
    error.value = 'Failed to load notes'
  } finally {
    loading.value = false
  }
}

async function sendQuickNote() {
  const text = quickText.value.trim()
  if (!text || isSending.value) return

  isSending.value = true
  try {
    const note = await props.createNote({ text, date: nowLocalDateTime() })
    if (props.analyticsEvent) capture(props.analyticsEvent)
    notes.value.unshift(note)
    quickText.value = ''
  } catch (err) {
    console.error('Failed to create note:', err)
  } finally {
    isSending.value = false
  }
}

function startEdit(note: Note) {
  editingId.value = note.id ?? null
  editTitle.value = note.title ?? ''
  editText.value = note.text ?? ''
  openMenuId.value = null
}

function cancelEdit() {
  editingId.value = null
  editTitle.value = ''
  editText.value = ''
}

async function saveEdit() {
  if (!editingId.value || !editText.value.trim() || isSavingEdit.value) return

  isSavingEdit.value = true
  try {
    const updated = await props.updateNote(editingId.value, {
      title: editTitle.value.trim() || undefined,
      text: editText.value.trim(),
    })
    const idx = notes.value.findIndex((n) => n.id === editingId.value)
    if (idx >= 0) notes.value[idx] = updated
    cancelEdit()
  } catch (err) {
    console.error('Failed to update note:', err)
  } finally {
    isSavingEdit.value = false
  }
}

function confirmDelete(noteId: string) {
  deletingId.value = noteId
  openMenuId.value = null
}

async function executeDelete() {
  if (!deletingId.value || isDeleting.value) return

  isDeleting.value = true
  try {
    await props.deleteNote(deletingId.value)
    notes.value = notes.value.filter((n) => n.id !== deletingId.value)
    deletingId.value = null
  } catch (err) {
    console.error('Failed to delete note:', err)
  } finally {
    isDeleting.value = false
  }
}

function toggleMenu(noteId: string) {
  openMenuId.value = openMenuId.value === noteId ? null : noteId
}

function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return ''
  try {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    }
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return date.toLocaleDateString('en-US', { weekday: 'long' })
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  } catch {
    return dateStr
  }
}

function handleQuickKeydown(e: KeyboardEvent) {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    sendQuickNote()
  }
}

onMounted(loadNotes)
</script>

<template>
  <div class="flex flex-col h-full">
    <!-- Header -->
    <div class="flex items-center justify-between p-4 border-b border-gray-200">
      <h3 class="text-lg font-semibold text-gray-900">Notes</h3>
      <button
        class="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1.5"
        @click="sortNewest = !sortNewest"
      >
        <font-awesome-icon :icon="['fas', 'arrow-down-short-wide']" class="text-xs" />
        {{ sortNewest ? 'Newest first' : 'Oldest first' }}
      </button>
    </div>

    <!-- Loading skeleton -->
    <div v-if="loading" class="p-4 space-y-4">
      <div v-for="i in 3" :key="i" class="animate-pulse space-y-2">
        <div class="flex justify-between">
          <div class="h-4 bg-gray-200 rounded w-24" />
          <div class="h-3 bg-gray-200 rounded w-16" />
        </div>
        <div class="h-3 bg-gray-200 rounded w-full" />
        <div class="h-3 bg-gray-200 rounded w-3/4" />
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="p-8 text-center">
      <p class="text-red-600 mb-3">{{ error }}</p>
      <AppButton variant="primary" size="md" @click="loadNotes">
        Try Again
      </AppButton>
    </div>

    <!-- Notes list -->
    <div v-else class="flex-1 overflow-y-auto">
      <!-- Empty state -->
      <div v-if="sortedNotes.length === 0" class="p-12 text-center">
        <div
          class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4"
        >
          <font-awesome-icon :icon="['fas', 'sticky-note']" class="text-2xl text-gray-400" />
        </div>
        <h4 class="font-medium text-gray-900 mb-1">{{ emptyMessage }}</h4>
        <p class="text-sm text-gray-500">Use the input below to add one</p>
      </div>

      <!-- Notes -->
      <ul v-else class="divide-y divide-gray-100">
        <li
          v-for="note in sortedNotes"
          :key="note.id"
          class="p-4 hover:bg-gray-50 transition-colors"
        >
          <!-- Edit mode -->
          <template v-if="editingId === note.id">
            <div class="space-y-3">
              <input
                v-model="editTitle"
                type="text"
                placeholder="Title (optional)"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                :style="{ '--tw-ring-color': accentColor }"
              />
              <textarea
                v-model="editText"
                rows="3"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent resize-none"
                :style="{ '--tw-ring-color': accentColor }"
              />
              <div class="flex justify-end gap-2">
                <AppButton variant="ghost" size="sm" @click="cancelEdit">
                  Cancel
                </AppButton>
                <AppButton
                  variant="primary"
                  size="sm"
                  :disabled="!editText.trim()"
                  :loading="isSavingEdit"
                  @click="saveEdit"
                >
                  Save
                </AppButton>
              </div>
            </div>
          </template>

          <!-- Display mode -->
          <template v-else>
            <div class="flex items-start justify-between gap-2">
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 mb-1">
                  <p v-if="note.title" class="font-medium text-gray-900 truncate">
                    {{ note.title }}
                  </p>
                  <span class="text-xs text-gray-400 whitespace-nowrap ml-auto">
                    {{ formatDate(note.date) }}
                  </span>
                </div>
                <p class="text-sm text-gray-600 line-clamp-3 whitespace-pre-wrap">
                  {{ note.text }}
                </p>
              </div>

              <!-- Menu -->
              <div class="relative flex-shrink-0">
                <button
                  class="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                  @click.stop="toggleMenu(note.id!)"
                >
                  <font-awesome-icon :icon="['fas', 'ellipsis-vertical']" class="text-sm" />
                </button>
                <div
                  v-if="openMenuId === note.id"
                  class="absolute right-0 top-8 w-32 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10"
                >
                  <button
                    class="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    @click="startEdit(note)"
                  >
                    <font-awesome-icon :icon="['fas', 'pen']" class="mr-2 text-gray-400" />
                    Edit
                  </button>
                  <button
                    class="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                    @click="confirmDelete(note.id!)"
                  >
                    <font-awesome-icon :icon="['fas', 'trash']" class="mr-2" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </template>
        </li>
      </ul>
    </div>

    <!-- Quick input bar (sticky at bottom) -->
    <div class="border-t border-gray-200 p-3 bg-white">
      <div class="flex items-center gap-2">
        <input
          v-model="quickText"
          type="text"
          :placeholder="placeholder"
          class="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent"
          :style="{ '--tw-ring-color': accentColor }"
          @keydown="handleQuickKeydown"
        />
        <button
          :disabled="!quickText.trim() || isSending"
          class="p-2.5 text-white rounded-lg hover:opacity-90 disabled:opacity-40 transition-colors"
          :style="{ backgroundColor: accentColor }"
          @click="sendQuickNote"
        >
          <font-awesome-icon
            :icon="['fas', isSending ? 'spinner' : 'paper-plane']"
            :class="{ 'animate-spin': isSending }"
            class="text-sm"
          />
        </button>
      </div>
    </div>

    <!-- Delete confirmation dialog -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition ease-out duration-200"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition ease-in duration-150"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div v-if="deletingId" class="fixed inset-0 z-50 flex items-center justify-center">
          <div class="absolute inset-0 bg-black/30" @click="deletingId = null" />
          <div class="relative bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
            <h3 class="text-lg font-semibold text-gray-900 mb-2">Delete note?</h3>
            <p class="text-sm text-gray-500 mb-6">This action cannot be undone.</p>
            <div class="flex justify-end gap-3">
              <AppButton variant="secondary" size="md" @click="deletingId = null">
                Cancel
              </AppButton>
              <AppButton
                variant="primary"
                size="md"
                class="!bg-red-600 hover:!bg-red-700"
                :loading="isDeleting"
                @click="executeDelete"
              >
                Delete
              </AppButton>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>
